"use server"

import bcrypt from "bcryptjs"
import { prisma } from "@/lib/prisma"
import { getBaseUrl } from "@/lib/base-url"
import { sendEmail } from "@/lib/services/email.service"
import { passwordResetService } from "@/lib/services/password-reset.service"
import { refreshTokenService } from "@/lib/services/refresh-token.service"
import {
  forgotPasswordSchema,
  resetPasswordSchema,
  type ForgotPasswordInput,
  type ResetPasswordInput,
} from "@/lib/schemas/auth.schema"

type ActionResult =
  | { ok: true }
  | { ok: false; error: string; fieldErrors?: Record<string, string[]> }

/**
 * Demande de reinitialisation. Pour eviter l'enumeration de comptes,
 * retourne TOUJOURS un succes generique, que l'email existe ou non.
 * N'envoie un email que si le compte existe ET possede un mot de passe
 * (les comptes Google-only n'ont pas de password : rien n'est envoye).
 */
export async function requestPasswordResetAction(
  input: ForgotPasswordInput,
): Promise<ActionResult> {
  const parsed = forgotPasswordSchema.safeParse(input)
  if (!parsed.success) {
    return { ok: false, error: "Adresse email invalide" }
  }

  const { email } = parsed.data

  const user = await prisma.user.findUnique({
    where: { email },
    select: { id: true, password: true, firstName: true },
  })

  // Compte inexistant ou compte Google-only (sans mot de passe) :
  // on ne fait rien mais on renvoie le meme message generique.
  if (user?.password) {
    const token = await passwordResetService.create(user.id)
    const baseUrl = await getBaseUrl()
    const resetUrl = `${baseUrl}/reset-password?token=${encodeURIComponent(token)}`

    const prenom = user.firstName ? ` ${user.firstName}` : ""
    const text = [
      `Bonjour${prenom},`,
      "",
      "Vous avez demandé la réinitialisation de votre mot de passe archifacil.",
      "Cliquez sur le lien ci-dessous pour choisir un nouveau mot de passe :",
      "",
      resetUrl,
      "",
      "Ce lien est valable 1 heure. Si vous n'êtes pas à l'origine de cette",
      "demande, ignorez simplement cet email : votre mot de passe reste inchangé.",
      "",
      "-- ",
      "archifacil — Architecture & plans électriques",
    ].join("\n")

    try {
      await sendEmail({
        to: email,
        subject: "Réinitialisation de votre mot de passe archifacil",
        text,
      })
    } catch (err) {
      // On loggue sans divulguer au client (message generique conserve).
      console.error("[password-reset] echec envoi email:", err)
    }
  }

  return { ok: true }
}

/**
 * Reinitialise le mot de passe a partir d'un token valide.
 */
export async function resetPasswordAction(
  input: ResetPasswordInput,
): Promise<ActionResult> {
  const parsed = resetPasswordSchema.safeParse(input)
  if (!parsed.success) {
    const fieldErrors: Record<string, string[]> = {}
    for (const issue of parsed.error.issues) {
      const key = String(issue.path[0])
      if (!fieldErrors[key]) fieldErrors[key] = []
      fieldErrors[key].push(issue.message)
    }
    return { ok: false, error: "Données invalides", fieldErrors }
  }

  const { token, password } = parsed.data

  const userId = await passwordResetService.verify(token)
  if (!userId) {
    return {
      ok: false,
      error: "Ce lien de réinitialisation est invalide ou a expiré.",
    }
  }

  const hashedPassword = await bcrypt.hash(password, 12)

  await prisma.user.update({
    where: { id: userId },
    data: { password: hashedPassword },
  })

  await passwordResetService.consume(token)

  // Securite : invalider toutes les sessions actives apres changement de mdp.
  await refreshTokenService.revokeAllForUser(userId)

  return { ok: true }
}
